import dateFormat from "dateformat";
import {
  UserSubscriptionStatus,
  Inventory,
  UserSubscriptionType,
  EonLinkedAccount,
  EonAccountProvider,
  UserNotification,
  UserNotificationType,
  SubscriptionActivationNotificationData,
  UserSubscription,
} from "@onmo/graphql/graphql";
import { Inventory as OldInventory } from "@onmo/graphql/legacyGraphql";
import { nth, find } from "lodash-es";
import { getDevice } from "./screen";
import { Devices } from "../types/screen";
import { getDayLeft } from "./time";
import { UserSubscriptionPopup } from "../types/user";

export const showMoreSpaceConsoleCard = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
) => {
  if (!status || !validUntil) return false;
  const expiredCondition = status === UserSubscriptionStatus.Expired;
  const pendingCondition =
    status === UserSubscriptionStatus.Subscribed && getDayLeft(validUntil) < 30;
  const dimensionCondition =
    getDevice() === Devices.Mobile || getDevice() === Devices.Tablet;

  return (expiredCondition || pendingCondition) && dimensionCondition;
};

export const isConsoleActive = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
) => {
  if (!status || !validUntil) return false;
  const activeConditions =
    status === UserSubscriptionStatus.Subscribed &&
    new Date() <= new Date(validUntil);
  const pendingCondition = status === UserSubscriptionStatus.Pending;

  return activeConditions || pendingCondition;
};

export const hasStatus = (status?: UserSubscriptionStatus) => {
  return (
    status === UserSubscriptionStatus.Subscribed ||
    status === UserSubscriptionStatus.Pending ||
    status === UserSubscriptionStatus.Expired ||
    status === UserSubscriptionStatus.Grace
  );
};

export const noLink = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
  isRecurring?: boolean,
) => {
  if (!status || !validUntil) return false;
  if (status !== UserSubscriptionStatus.Subscribed) return false;
  return getDayLeft(validUntil) >= 30 || Boolean(isRecurring);
};

export const getStatusInfo = (
  status?: UserSubscriptionStatus,
  validUntil?: string,
  isRecurring?: boolean,
): "expire-soon" | "subscribed" | "expired" | "not-subscribed" | "grace" => {
  if (!status || !validUntil) return "not-subscribed";
  if (
    status === UserSubscriptionStatus.Subscribed &&
    getDayLeft(validUntil) < 30 &&
    !isRecurring
  )
    return "expire-soon";
  if (
    status === UserSubscriptionStatus.Subscribed &&
    new Date() <= new Date(validUntil)
  )
    return "subscribed";
  if (status === UserSubscriptionStatus.Grace) return "grace";
  return "expired";
};

export const isActivatedSubscription = (
  subscription?: UserSubscription | null,
): boolean => {
  if (!subscription) return false;
  const { status, validUntil, renewalDate } = subscription;
  if (status !== UserSubscriptionStatus.Subscribed) return false;
  if (!validUntil) return false;

  const now = new Date();
  const dates = [validUntil, renewalDate]
    .filter((date) => Boolean(date))
    .map((d) => new Date(d));

  return dates.every((date) => now < date);
};

export const getInventory = (
  avatar?: string,
  inventories?: (Inventory | OldInventory)[],
) => {
  if (!avatar || !inventories) return;
  const inventoryId = nth(avatar.split("/"), -2);
  return find(inventories, { id: inventoryId });
};

export const getSubscriptionStatus = (
  status?: UserSubscriptionStatus,
  isRecurring?: boolean,
  type?: UserSubscriptionType,
  validUntil?: string | null,
): UserSubscriptionPopup => {
  if (
    (type === UserSubscriptionType.Voucher ||
      type === UserSubscriptionType.Onetime) &&
    validUntil &&
    new Date(validUntil) > new Date()
  )
    return "continue";
  if (
    (type === UserSubscriptionType.Autopay && !isRecurring) ||
    status === UserSubscriptionStatus.Expired
  )
    return "resume";
  if (status === UserSubscriptionStatus.NotSubscribed) return "subscribe";
  if (status === UserSubscriptionStatus.Subscribed && isRecurring)
    return "cancel";
  if (status === UserSubscriptionStatus.Grace) return "grace";
  return null;
};

export const getPopupDetail = (
  popupStatus: UserSubscriptionPopup,
  validUntil: string | null,
  renewalDate: string | null,
) => {
  if (popupStatus === "continue")
    return {
      labelBtn: "Continue with Subscription",
      labelTime:
        validUntil && new Date(validUntil) < new Date()
          ? "Expired on"
          : "Valid Until",
      time: validUntil && dateFormat(new Date(validUntil), "dd/mm/yyyy"),
    };
  if (popupStatus === "resume")
    return {
      labelBtn: "Resume Subscription",
      labelTime:
        validUntil && new Date(validUntil) < new Date()
          ? "Expired on"
          : "Expires on",
      time: validUntil && dateFormat(new Date(validUntil), "dd/mm/yyyy"),
    };
  if (popupStatus === "subscribe")
    return {
      labelBtn: "Subscribe",
    };
  if (popupStatus === "cancel")
    return {
      labelBtn: "Cancel Subscription",
      labelTime: "Renews on",
      time: renewalDate && dateFormat(new Date(renewalDate), "dd/mm/yyyy"),
    };
  if (popupStatus === "grace")
    return {
      labelBtn: "Cancel Subscription",
      labelTime: "Payment Failed",
      time: renewalDate && dateFormat(new Date(renewalDate), "dd/mm/yyyy"),
    };
  return {};
};

export const getEonSteamAccount = (eonLinkedAccounts: EonLinkedAccount[]) => {
  return find(eonLinkedAccounts, { provider: EonAccountProvider.Steam });
};

export const isFounderNotifications = (notifications: UserNotification[]) => {
  const subActivated = find(notifications, {
    type: UserNotificationType.SubscriptionActivated,
  });
  const subBonus = find(notifications, {
    type: UserNotificationType.SubscriptionBonusGranted,
  });

  return {
    isFounder: (
      subActivated?.data as SubscriptionActivationNotificationData
    )?.earnedTags?.includes("op_founders"),
    isBonus: !!subBonus,
  };
};

export const getDurationSubscription = (duration: number | undefined) => {
  if (!duration) return "Month";
  if (duration >= 365) {
    const year = Math.floor(duration / 365);
    return year > 1 ? `${year} years` : `Year`;
  } else if (duration >= 30) {
    const month = Math.floor(duration / 30);
    return month > 1 ? `${month} months` : "Month";
  } else {
    return duration > 1 ? `${duration} days` : "Day";
  }
};
